import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Sparkles, 
  Check, 
  Calendar,
  Loader2,
  EyeOff
} from 'lucide-react';
import { useFestivalTheme, festivalThemes, FestivalType } from '../contexts/FestivalThemeContext';

interface FestivalThemeSelectorProps {
  onThemeChange?: (themeId: FestivalType) => void;
}

export default function FestivalThemeSelector({ onThemeChange }: FestivalThemeSelectorProps) {
  const { currentThemeId, setTheme, isLoading } = useFestivalTheme();
  const [saving, setSaving] = useState(false);

  const handleThemeSelect = async (themeId: FestivalType) => {
    console.log('🎄 Theme clicked:', themeId);
    console.log('🎄 Current theme:', currentThemeId);
    
    if (themeId === currentThemeId) {
      console.log('🎄 Same theme selected, skipping');
      return;
    }
    
    setSaving(true);
    console.log('🎄 Saving theme...');
    
    try {
      await setTheme(themeId);
      console.log('🎄 Theme saved successfully');
      onThemeChange?.(themeId);
    } catch (error) {
      console.error('🎄 Error in handleThemeSelect:', error);
    } finally {
      setSaving(false);
    }
  };

  const themeCategories = {
    seasonal: ['christmas', 'new_year', 'easter', 'thanksgiving', 'halloween'] as FestivalType[],
    special: ['valentines', 'mothers_day', 'fathers_day', 'independence_day', 'diwali'] as FestivalType[],
  };

  if (isLoading) {
    return (
      <div className="p-12 text-center">
        <Loader2 className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-4" />
        <p className="text-gray-600">Loading theme settings...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Debug Info - Remove in production */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm">
        <p><strong>Debug:</strong> Current Theme ID: <code className="bg-yellow-100 px-1 rounded">{currentThemeId}</code></p>
        <p>Saving: {saving ? 'Yes' : 'No'}</p>
        <p className="text-xs text-gray-500 mt-1">Check browser console (F12) for click logs</p>
      </div>

      {/* Current Theme Display */}
      <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl p-6 border border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div 
              className="w-16 h-16 rounded-2xl flex items-center justify-center text-3xl"
              style={{
                background: currentThemeId !== 'none' 
                  ? `linear-gradient(135deg, ${festivalThemes[currentThemeId].gradientFrom}, ${festivalThemes[currentThemeId].gradientTo})`
                  : '#f3f4f6',
              }}
            >
              {festivalThemes[currentThemeId].emoji || '✨'}
            </div>
            <div>
              <p className="text-sm text-gray-500 font-medium">Current Theme</p>
              <h3 className="text-2xl font-bold text-gray-900">
                {festivalThemes[currentThemeId].name}
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                {festivalThemes[currentThemeId].description}
              </p>
            </div>
          </div>
          
          {currentThemeId !== 'none' && (
            <button
              onClick={() => handleThemeSelect('none')}
              disabled={saving}
              className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium rounded-lg transition-colors flex items-center gap-2"
            >
              <EyeOff className="w-4 h-4" />
              Disable Theme
            </button>
          )}
        </div>
      </div>

      {/* Default / No Theme Option */}
      <div className="mb-6">
        <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3 flex items-center gap-2">
          <Sparkles className="w-4 h-4" />
          Default
        </h4>
        <button
          type="button"
          onClick={() => {
            console.log('🎄 No Festival Theme button clicked');
            handleThemeSelect('none');
          }}
          disabled={saving}
          className={`w-full p-4 rounded-xl border-2 transition-all flex items-center gap-4 cursor-pointer ${
            currentThemeId === 'none'
              ? 'border-blue-500 bg-blue-50'
              : 'border-gray-200 hover:border-gray-300 bg-white'
          }`}
        >
          <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center text-2xl">
            ✨
          </div>
          <div className="flex-1 text-left">
            <h4 className="font-bold text-gray-900">No Festival Theme</h4>
            <p className="text-sm text-gray-500">Default app appearance</p>
          </div>
          {currentThemeId === 'none' && (
            <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center">
              <Check className="w-5 h-5 text-white" />
            </div>
          )}
        </button>
      </div>

      {/* Seasonal Festivals */}
      <div>
        <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3 flex items-center gap-2">
          <Calendar className="w-4 h-4" />
          Seasonal Festivals
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {themeCategories.seasonal.map((themeId) => {
            const theme = festivalThemes[themeId];
            const isSelected = currentThemeId === themeId;
            
            return (
              <button
                key={themeId}
                type="button"
                onClick={() => {
                  console.log(`🎄 ${theme.name} button clicked`);
                  handleThemeSelect(themeId);
                }}
                disabled={saving}
                className={`relative p-4 rounded-xl border-2 transition-all text-left overflow-hidden cursor-pointer ${
                  isSelected
                    ? 'border-blue-500 shadow-lg'
                    : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
                } ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}
                style={{
                  background: isSelected 
                    ? `linear-gradient(135deg, ${theme.gradientFrom}15, ${theme.gradientTo}15)`
                    : 'white',
                }}
              >
                {/* Theme Preview Gradient */}
                <div 
                  className="absolute top-0 left-0 right-0 h-1"
                  style={{
                    background: `linear-gradient(90deg, ${theme.gradientFrom}, ${theme.gradientTo})`,
                  }}
                />
                
                <div className="flex items-start gap-3">
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl flex-shrink-0"
                    style={{
                      background: `linear-gradient(135deg, ${theme.gradientFrom}, ${theme.gradientTo})`,
                    }}
                  >
                    {theme.emoji}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-bold text-gray-900 truncate">{theme.name}</h4>
                    <p className="text-xs text-gray-500 mt-1 line-clamp-2">{theme.description}</p>
                    
                    {/* Decorative emojis preview */}
                    <div className="flex items-center gap-1 mt-2 overflow-hidden">
                      {theme.decorativeEmojis.slice(0, 4).map((emoji, i) => (
                        <span key={i} className="text-sm opacity-60">{emoji}</span>
                      ))}
                    </div>
                  </div>
                  
                  {isSelected && (
                    <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                  )}
                </div>

                {/* Particle type indicator */}
                <div className="mt-3 flex items-center gap-2">
                  <span 
                    className="text-xs px-2 py-1 rounded-full font-medium"
                    style={{
                      backgroundColor: `${theme.primaryColor}20`,
                      color: theme.primaryColor,
                    }}
                  >
                    {theme.particleType === 'none' ? 'No effects' : `${theme.particleType} effect`}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Special Occasions */}
      <div>
        <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3 flex items-center gap-2">
          <Sparkles className="w-4 h-4" />
          Special Occasions
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {themeCategories.special.map((themeId) => {
            const theme = festivalThemes[themeId];
            const isSelected = currentThemeId === themeId;
            
            return (
              <button
                key={themeId}
                type="button"
                onClick={() => {
                  console.log(`🎄 ${theme.name} button clicked`);
                  handleThemeSelect(themeId);
                }}
                disabled={saving}
                className={`relative p-4 rounded-xl border-2 transition-all text-left overflow-hidden cursor-pointer ${
                  isSelected
                    ? 'border-blue-500 shadow-lg'
                    : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
                } ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}
                style={{
                  background: isSelected 
                    ? `linear-gradient(135deg, ${theme.gradientFrom}15, ${theme.gradientTo}15)`
                    : 'white',
                }}
              >
                {/* Theme Preview Gradient */}
                <div 
                  className="absolute top-0 left-0 right-0 h-1"
                  style={{
                    background: `linear-gradient(90deg, ${theme.gradientFrom}, ${theme.gradientTo})`,
                  }}
                />
                
                <div className="flex items-start gap-3">
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl flex-shrink-0"
                    style={{
                      background: `linear-gradient(135deg, ${theme.gradientFrom}, ${theme.gradientTo})`,
                    }}
                  >
                    {theme.emoji}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-bold text-gray-900 truncate">{theme.name}</h4>
                    <p className="text-xs text-gray-500 mt-1 line-clamp-2">{theme.description}</p>
                    
                    {/* Decorative emojis preview */}
                    <div className="flex items-center gap-1 mt-2 overflow-hidden">
                      {theme.decorativeEmojis.slice(0, 4).map((emoji, i) => (
                        <span key={i} className="text-sm opacity-60">{emoji}</span>
                      ))}
                    </div>
                  </div>
                  
                  {isSelected && (
                    <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                  )}
                </div>

                {/* Particle type indicator */}
                <div className="mt-3 flex items-center gap-2">
                  <span 
                    className="text-xs px-2 py-1 rounded-full font-medium"
                    style={{
                      backgroundColor: `${theme.primaryColor}20`,
                      color: theme.primaryColor,
                    }}
                  >
                    {theme.particleType === 'none' ? 'No effects' : `${theme.particleType} effect`}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Saving Indicator */}
      <AnimatePresence>
        {saving && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="fixed bottom-6 right-6 bg-blue-600 text-white px-6 py-3 rounded-xl shadow-lg flex items-center gap-3 z-50"
          >
            <Loader2 className="w-5 h-5 animate-spin" />
            <span className="font-medium">Saving theme...</span>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
